import * as React from 'react';
import { storiesOf } from '@storybook/react';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import { CheckboxGroup, CheckboxGroupLayout, CheckboxGroupProps } from '..';
import { Checkbox, CheckboxTheme } from '../Checkbox';
import { classes } from './CheckboxGroup.visual.st.css';
import { snap, story, visualize } from 'storybook-snapper';
import { VisualAsync } from '../../test/visual/VisualAsync';
import { checkboxGroupDriverFactory } from './CheckboxGroup.driver';

class CheckboxGroupVisual extends React.Component<
  CheckboxGroupProps & { style: React.CSSProperties }
> {
  render() {
    return (
      <VisualTestContainer>
        <div style={this.props.style}>
          <CheckboxGroup {...this.props} />
          {this.props.error && this.props.errorText && (
            <div style={{ border: '1px dashed black' }}>
              An element below to show the spacing
            </div>
          )}
        </div>
      </VisualTestContainer>
    );
  }
}

const checkboxEl = (
  <Checkbox key={1} checked={false} onChange={() => {}} label="hello" />
);
const checkboxElChecked = (
  <Checkbox key={1} checked onChange={() => {}} label="hello" />
);
const checkboxElBox = (
  <Checkbox
    theme={CheckboxTheme.Box}
    key={1}
    checked={false}
    onChange={() => {}}
    label="hello"
  />
);
const checkboxElBoxChecked = (
  <Checkbox
    theme={CheckboxTheme.Box}
    key={1}
    checked
    onChange={() => {}}
    label="hello"
  />
);

const tests = [
  {
    describe: 'basic',
    its: [
      {
        it: 'default',
        props: {
          children: [checkboxEl, checkboxEl],
        },
      },
      {
        it: 'with label',
        props: {
          children: [checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
        },
      },
      {
        it: 'with fluid',
        props: {
          children: [checkboxEl],
          label: "No worries. I'm a label.",
          fluid: true,
        },
      },
      {
        it: 'with horizontal layout',
        props: {
          children: [checkboxEl, checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          layout: CheckboxGroupLayout.Horizontal,
        },
      },
      {
        it: 'old error / with horizontal layout',
        props: {
          children: [checkboxEl, checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          layout: CheckboxGroupLayout.Horizontal,
          errorText: "I'm an error",
          error: true,
        },
      },
      {
        it: 'with disabled',
        props: {
          children: [checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          disabled: true,
        },
      },
      {
        it: 'old error without a message',
        props: {
          children: [checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          error: true,
        },
      },
      {
        it: 'old error with a message',
        props: {
          children: [checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          error: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'old error with a message and with checked checkbox',
        props: {
          children: [checkboxEl, checkboxElChecked, checkboxEl],
          label: "No worries. I'm a label.",
          error: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'new error message without a message',
        props: {
          children: [checkboxEl, checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
        },
      },
      {
        it: 'new error message with a message',
        props: {
          children: [checkboxEl, checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'new error message / horizontal layout',
        props: {
          children: [checkboxEl, checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          layout: CheckboxGroupLayout.Horizontal,
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'new error message with checked checkbox',
        props: {
          children: [checkboxEl, checkboxElChecked, checkboxEl],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
        },
      },
    ],
  },
  {
    describe: 'box theme',
    its: [
      {
        it: 'default',
        props: {
          children: [checkboxElBox, checkboxElBox],
        },
      },
      {
        it: 'with label',
        props: {
          children: [checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
        },
      },
      {
        it: 'with horizontal layout',
        props: {
          children: [checkboxElBox, checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
          layout: CheckboxGroupLayout.Horizontal,
        },
      },
      {
        it: 'old error / with horizontal layout',
        props: {
          children: [checkboxElBox, checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
          layout: CheckboxGroupLayout.Horizontal,
          errorText: "I'm an error",
          error: true,
        },
      },
      {
        it: 'with disabled',
        props: {
          children: [checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
          disabled: true,
        },
      },
      {
        it: 'old error without a message',
        props: {
          children: [checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
          error: true,
        },
      },
      {
        it: 'old error with a message',
        props: {
          children: [checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
          error: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'old error with a message and with checked checkbox',
        props: {
          children: [checkboxElBox, checkboxElBoxChecked, checkboxElBox],
          label: "No worries. I'm a label.",
          error: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'new error message without a message',
        props: {
          children: [checkboxElBox, checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
        },
      },
      {
        it: 'new error message with a message',
        props: {
          children: [checkboxElBox, checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'new error message / horizontal layout',
        props: {
          children: [checkboxElBox, checkboxElBox, checkboxElBox],
          label: "No worries. I'm a label.",
          layout: CheckboxGroupLayout.Horizontal,
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
        },
      },
      {
        it: 'new error message with checked checkbox',
        props: {
          children: [checkboxElBox, checkboxElBoxChecked, checkboxElBox],
          label: "No worries. I'm a label.",
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
        },
      },
    ],
  },
  {
    describe: 'with overrides',
    its: [
      {
        it: 'with label',
        props: {
          children: [checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          className: classes.root,
        },
      },
      {
        it: 'new error message / horizontal layout',
        props: {
          children: [checkboxElBox, checkboxElBoxChecked, checkboxElBox],
          label: "No worries. I'm a label.",
          layout: CheckboxGroupLayout.Horizontal,
          error: true,
          newErrorMessage: true,
          errorText: "I'm error text",
          className: classes.root,
        },
      },
      {
        it: 'with increased horizontal spacing',
        props: {
          children: [checkboxEl, checkboxEl, checkboxEl],
          label: "No worries. I'm a label.",
          layout: CheckboxGroupLayout.Horizontal,
          className: classes.horizontalSpacing,
        },
      },
    ],
  },
  {
    describe: 'wst theme',
    its: [
      {
        it: 'label / font size',
        props: {
          label: "No worries. I'm a label. Special size",
          style: { '--wst-paragraph-2-font-size': '120px' },
        },
      },
      {
        it: 'label / font family',
        props: {
          label: "No worries. I'm a label. Special font",
          style: {
            '--wst-paragraph-2-font-family': 'Courier New, serif',
          },
        },
      },
      {
        it: 'label / font color',
        props: {
          label: "No worries. I'm a label. Special color",
          style: { '--wst-paragraph-2-color-rgb': '0,254,0' },
        },
      },
      {
        it: 'label / font variant',
        props: {
          label: "No worries. I'm a label. Special variant",
          style: {
            '--wst-paragraph-2-font-variant': 'small-caps',
          },
        },
      },
      {
        it: 'label / font style',
        props: {
          label: "No worries. I'm a label. Special style",
          style: {
            '--wst-paragraph-2-font-style': 'italic',
          },
        },
      },
    ],
  },
];

tests.forEach(({ describe, its }) => {
  its.forEach(({ it, props }) => {
    storiesOf(`CheckboxGroup/${describe}`, module).add(it, () => (
      <CheckboxGroupVisual {...props} />
    ));
  });
});

visualize('CheckboxGroup', () => {
  [tests[0], tests[1]].forEach(({ describe, its }) => {
    story(`${describe} / focus`, () => {
      its.forEach(({ it, props }) => {
        snap(`${it}`, (done) => (
          <VisualAsync
            driverFactory={checkboxGroupDriverFactory}
            onDone={done}
            action={(driver) => driver.clickAt(1)}
          >
            <CheckboxGroup {...props} />
          </VisualAsync>
        ));
      });
    });
  });
});
